import { useAuth0 } from '@auth0/auth0-react';
import { useSWRConfig } from 'swr';
import useSWRImmutable from 'swr/immutable';
import ResourceNotFoundError from '@/Utils/HTTP/Errors/ResourceNotFoundError';
import http from '@/Utils/HTTP/NewHttpUtils';

export interface CompanyLookupInfoResponse {
    name: string;
    domain: string | null;
    description: string | null;
    website: string | null;
    type: string;
    headquarter: string | null;
    employeesCount: number | null;
    foundedAt: number | null;
    industry: string | null;
    revenueTextFormatted: string | null;
    revenue: number | null;
    currency: string | null;
    linkedInUrl: string | null;
}

export interface CompanyLookupInfoRequest {
    companyName: string;
    companyDomain: string | null;
    companyLinkedInUrl: string | null;
}

export const useCompanyLookup = (
    companyName: string,
    companyDomain: string | null,
    companyLinkedInUrl: string | null,
) => {
    const { getAccessTokenSilently } = useAuth0();
    const { cache } = useSWRConfig();

    const key: [string, string, string | null, string | null] | null = companyName
        ? ['enriched-profiles/company-lookup', companyName, companyDomain, companyLinkedInUrl]
        : null;

    return useSWRImmutable(
        key,
        async ([path]) => {
            const cacheKey = JSON.stringify(key);
            const cachedCall = cache.get(cacheKey);
            if (cachedCall?.data) {
                return cachedCall.data as CompanyLookupInfoResponse;
            }

            const payload: CompanyLookupInfoRequest = {
                companyName,
                companyDomain,
                companyLinkedInUrl,
            };

            const response = await http.post<CompanyLookupInfoResponse>({
                path,
                body: payload,
                accessToken: await getAccessTokenSilently(),
            });

            return response;
        },
        {
            onError: (err) => err,
            shouldRetryOnError: (err) => !(err instanceof ResourceNotFoundError),
        },
    );
};
